import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { tripsApi } from '../../../api/trips.api';
import { TRIP_SHARE_SECTIONS, TRIP_SHARE_LEGACY_SECTIONS, type Trip, type TripShareSection } from '../../../types';
import { Icon, Globe, Copy, Check, AlertTriangle } from '../../ui/Icon';
import { toast } from '../../ui/Toast';

/**
 * Showing the trip to somebody who is NOT on it.
 *
 * The third thing the Manage dialog can do, and the one that is easy to confuse
 * with the other two. A `VIEWER` is a traveller who does not edit: they count in
 * the per-person split, they get their own suitcase, they are in the head count.
 * Whoever opens this link is none of that — they look, and that is all. The copy
 * has to keep saying so, because the difference is invisible until somebody's
 * "per person" figure comes out wrong.
 *
 * The toggles are a preference, not a permission: they decide what the server is
 * allowed to QUERY for an anonymous visitor (ADR-0012). Two things deliberately
 * have no checkbox here — the Notes tab and packing — because the owner does not
 * get to publish what belongs to somebody else (ADR-0003). Attachments and
 * booking locators have none either, at any setting.
 */

interface Props {
  trip: Trip;
  onTripUpdate: (t: Trip) => void;
}

export default function ShareLinkPanel({ trip, onTripUpdate }: Props) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [savingSection, setSavingSection] = useState<TripShareSection | null>(null);

  const sections = trip.shareSections ?? [];
  const isUntouchedLegacyLink = sections.length === TRIP_SHARE_LEGACY_SECTIONS.length
    && TRIP_SHARE_LEGACY_SECTIONS.every(x => sections.includes(x));
  const shareUrl = trip.shareToken ? `${window.location.origin}/share/${trip.shareToken}` : '';

  async function toggleLink() {
    setLoading(true);
    try {
      const next = await tripsApi.toggleShare(trip.id);
      onTripUpdate({ ...trip, ...next });
      toast(t(next.shareToken ? 'trip.share.enabledToast' : 'trip.share.disabledToast'), 'success');
    } finally { setLoading(false); }
  }

  async function toggleSection(section: TripShareSection) {
    const next = sections.includes(section)
      ? sections.filter(s => s !== section)
      : [...sections, section];
    setSavingSection(section);
    try {
      const saved = await tripsApi.updateShareSections(trip.id, next);
      onTripUpdate({ ...trip, ...saved });
    } finally { setSavingSection(null); }
  }

  return (
    <div className="pt-4 border-t border-slate-100 dark:border-slate-700/60">
      <p className="label mb-1 flex items-center gap-1.5">
        <Icon as={Globe} size={13} className="text-slate-400" />
        {t('trip.share.panelTitle')}
      </p>
      <p className="text-[11px] text-slate-500 dark:text-slate-400 mb-3 leading-relaxed">
        {t('trip.share.panelHint')}
      </p>

      {!trip.shareToken ? (
        <button
          disabled={loading}
          onClick={toggleLink}
          className="btn-primary w-full gap-1.5 text-xs py-1.5"
        >
          <Icon as={Globe} size={14} />
          {loading ? t('trip.share.creating') : t('trip.share.enable')}
        </button>
      ) : (
        <>
          <div className="flex gap-1.5">
            <input
              readOnly
              value={shareUrl}
              onFocus={e => e.currentTarget.select()}
              className="flex-1 min-w-0 text-xs bg-slate-50 dark:bg-slate-800/60 border border-slate-200 dark:border-slate-700 rounded-lg px-2.5 py-1.5 text-slate-600 dark:text-slate-300 truncate"
            />
            <button
              onClick={async () => {
                await navigator.clipboard.writeText(shareUrl);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }}
              aria-label={t('common.copy')}
              className="btn-icon w-8 h-8 shrink-0 text-teal-600"
            >
              {copied ? <Icon as={Check} size={14} /> : <Icon as={Copy} size={14} />}
            </button>
          </div>

          <p className="font-mono text-[10px] uppercase tracking-widest text-slate-400 mt-4 mb-2">
            {t('trip.share.sectionsTitle')}
          </p>

          {/* Only when the set is EXACTLY what the migration backfilled. Showing
              it whenever a box is unticked told the owner their link predated
              these options right after they unticked one themselves. */}
          {isUntouchedLegacyLink && (
            <p className="text-2xs text-slate-400 mb-2 leading-relaxed">{t('trip.share.sectionsHint')}</p>
          )}

          <div className="grid grid-cols-2 gap-x-3 gap-y-0.5">
            {/* ADR-0044: agency staff don't read the diary, so they can't publish it. */}
            {TRIP_SHARE_SECTIONS.filter(section => section !== 'DIARY' || !trip.viewerIsAgency).map(section => (
              <label
                key={section}
                className="flex items-center gap-2 px-1 py-1 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-800/60 cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={sections.includes(section)}
                  disabled={savingSection !== null}
                  onChange={() => toggleSection(section)}
                  className="accent-teal-600 cursor-pointer shrink-0"
                />
                <span className="text-xs text-slate-600 dark:text-slate-300 leading-tight">
                  {t(`trip.share.section.${section}`)}
                </span>
              </label>
            ))}
          </div>

          {/* The bucket does not sign URLs, so a photo link that has been copied
              keeps working after the toggle goes off. This is the only place
              anybody would read it. */}
          {sections.includes('PHOTOS') && (
            <p className="flex items-start gap-1.5 text-2xs text-amber-600 dark:text-amber-500 mt-2 leading-relaxed">
              <Icon as={AlertTriangle} size={12} className="shrink-0 mt-px" />
              {t('trip.share.photosWarning')}
            </p>
          )}

          {sections.includes('DIARY') && (
            <p className="text-2xs text-slate-500 dark:text-slate-400 mt-2 leading-relaxed">{t('trip.share.diaryHint')}</p>
          )}

          <button
            disabled={loading}
            onClick={toggleLink}
            className="mt-3 text-xs text-red-500 hover:text-red-700 font-medium cursor-pointer"
          >
            {t('trip.share.disable')}
          </button>
        </>
      )}
    </div>
  );
}
